import { format, subYears } from "date-fns";
import { useMediaQuery } from "react-responsive";
import * as XLSX from "xlsx";
import { es } from "date-fns/locale";

const FiltroEventos = ({
  setFilter,
  filter,
  fetch,
  onClick,
  onChange,
  provincias,
  categorias,
  setData,
  salas,
  setSalas,
  setLoading,
  loading,
  user,
  anterior,
  setAnterior,
  eventos,
}) => {
  const isDesktop = useMediaQuery({ minWidth: 768 });

  const generarImagenes = async () => {
    try {
      setLoading(true);

      if (!eventos || !eventos.data || eventos.data.length === 0) {
        alert("No hay eventos para generar imágenes");
        setLoading(false);
        return;
      }

      // Ordenar eventos por fecha y agrupar por día
      const eventosOrdenados = [];

      eventos.data.forEach((evento) => {
        if (evento.fechas && evento.fechas.length > 0) {
          evento.fechas.forEach((fecha) => {
            eventosOrdenados.push({
              ...evento,
              fecha: fecha.fecha,
              hora: fecha.hora || "Sin hora",
            });
          });
        } else {
          eventosOrdenados.push({
            ...evento,
            fecha: evento.created_at,
            hora: "Sin hora",
          });
        }
      });

      // Ordenar por fecha
      eventosOrdenados.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

      // Agrupar por día
      const eventosPorDia = {};
      eventosOrdenados.forEach((evento) => {
        const fechaEvento = new Date(evento.fecha);
        const diaSemana = format(fechaEvento, "EEEE", { locale: es });
        const diaNumero = format(fechaEvento, "d", { locale: es });
        const mes = format(fechaEvento, "MMMM", { locale: es });
        const año = format(fechaEvento, "yyyy", { locale: es });

        const claveDia = `${diaSemana.toUpperCase()} ${diaNumero} DE ${mes.toUpperCase()} DE ${año}`;

        if (!eventosPorDia[claveDia]) {
          eventosPorDia[claveDia] = [];
        }

        eventosPorDia[claveDia].push(evento);
      });

      // Generar imágenes (máximo 4 eventos por imagen para mejor visualización)
      const imagenes = [];
      let totalImagenes = 0;

      // Calcular total de imágenes primero
      for (const eventos of Object.values(eventosPorDia)) {
        totalImagenes += Math.ceil(eventos.length / 4);
      }

      let numeroPaginaGlobal = 1;

      for (const [dia, eventos] of Object.entries(eventosPorDia)) {
        // Dividir eventos en grupos de 4
        for (let i = 0; i < eventos.length; i += 4) {
          const eventosGrupo = eventos.slice(i, i + 4);
          const canvas = await crearCanvas(dia, eventosGrupo, numeroPaginaGlobal, totalImagenes);
          imagenes.push({ dia, canvas, numero: numeroPaginaGlobal });
          numeroPaginaGlobal++;
        }
      }

      // Descargar todas las imágenes
      imagenes.forEach(({ dia, canvas, numero }, index) => {
        setTimeout(() => {
          const link = document.createElement("a");
          link.download = `eventos_${dia.replace(/\s+/g, "_")}_${numero}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        }, index * 300); // Delay para evitar problemas con múltiples descargas
      });

      setLoading(false);
      alert(`Se generaron ${imagenes.length} imágenes`);
    } catch (error) {
      console.error("Error al generar imágenes:", error);
      alert("Error al generar las imágenes");
      setLoading(false);
    }
  };

  const crearCanvas = (dia, eventos, numeroPagina, totalPaginas) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1350;
      const ctx = canvas.getContext("2d");

      // Fondo gris claro con patrón de cuadrados
      ctx.fillStyle = "#F5F5F5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Dibujar patrón de fondo (cuadrados sutiles)
      const gridSize = 40;
      ctx.fillStyle = "#E8E8E8";
      for (let x = 0; x < canvas.width; x += gridSize * 2) {
        for (let y = 0; y < canvas.height; y += gridSize * 2) {
          ctx.fillRect(x, y, gridSize, gridSize);
          ctx.fillRect(x + gridSize, y + gridSize, gridSize, gridSize);
        }
      }

      // Contador de páginas (esquina superior derecha)
      ctx.fillStyle = "#808080";
      ctx.font = "bold 32px Arial, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(`${numeroPagina}/${totalPaginas}`, canvas.width - 50, 60);

      // Título del día - extraer solo día y número
      const partesDia = dia.split(" DE ");
      const diaSemanaYNumero = partesDia[0]; // "VIERNES 14"

      ctx.fillStyle = "#2C2C2C";
      ctx.font = "bold 72px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(diaSemanaYNumero, canvas.width / 2, 140);

      // Configuración inicial
      let yPosition = 230;
      const padding = 70;
      const lineHeight = 35;
      const separadorHeight = 2;

      // Dibujar cada evento
      eventos.forEach((evento, index) => {
        // Hora (negro) con "hs"
        ctx.fillStyle = "#2C2C2C";
        ctx.font = "bold 32px Arial, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(`${evento.hora} hs`, padding, yPosition);
        yPosition += lineHeight + 8;

        // Nombre del evento (violeta)
        ctx.fillStyle = "#7C3AED"; // Color violeta más cercano al diseño
        ctx.font = "bold 38px Arial, sans-serif";

        // Manejo de texto largo - dividir en líneas
        const maxWidth = canvas.width - padding * 2;
        const palabras = evento.nombre.toUpperCase().split(" ");
        let linea = "";
        let lineas = [];

        palabras.forEach((palabra) => {
          const testLinea = linea + palabra + " ";
          const metrics = ctx.measureText(testLinea);
          if (metrics.width > maxWidth && linea !== "") {
            lineas.push(linea);
            linea = palabra + " ";
          } else {
            linea = testLinea;
          }
        });
        lineas.push(linea);

        // Dibujar líneas del nombre del evento
        lineas.forEach((lineaTexto) => {
          ctx.fillText(lineaTexto.trim(), padding, yPosition);
          yPosition += lineHeight + 5;
        });

        yPosition += 3;

        // Nombre de la sala (negro)
        ctx.fillStyle = "#2C2C2C";
        ctx.font = "bold 28px Arial, sans-serif";
        ctx.fillText(evento.nombre_sitio || "Sin sala", padding, yPosition);
        yPosition += lineHeight;

        // Dirección (gris)
        ctx.fillStyle = "#6B7280";
        ctx.font = "normal 24px Arial, sans-serif";
        const direccion = evento.direccion || "Sin dirección";
        ctx.fillText(direccion, padding, yPosition);
        yPosition += lineHeight + 20;

        // Línea separadora (violeta)
        if (index < eventos.length - 1) {
          ctx.fillStyle = "#7C3AED";
          ctx.fillRect(padding, yPosition, canvas.width - padding * 2, separadorHeight);
          yPosition += separadorHeight + 30;
        }
      });

      // Logo de Antesala en la parte inferior
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.onload = () => {
        // Dibujar logo centrado en la parte inferior
        const logoWidth = 200;
        const logoHeight = 60;
        const logoX = (canvas.width - logoWidth) / 2;
        const logoY = canvas.height - 120;

        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        resolve(canvas);
      };

      logoImg.onerror = () => {
        // Si falla la carga del logo, dibujar texto "Antesala"
        ctx.fillStyle = "#2C2C2C";
        ctx.font = "bold 48px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Antesala", canvas.width / 2, canvas.height - 80);
        resolve(canvas);
      };

      // Intentar cargar el logo
      logoImg.src = "/assets/img/marca/antesala-logo.svg";
    });
  };

  const exportarExcel = async () => {
    try {
      setLoading(true);

      // Obtener todos los eventos sin paginación
      const params = {
        ...filter,
        per_page: 1000, // Obtener todos los eventos
      };

      const response = await fetch(
        (data) => data,
        params,
        "/eventos",
        null,
        setLoading
      );

      if (!eventos || !eventos.data || eventos.data.length === 0) {
        alert("No hay eventos para exportar");
        setLoading(false);
        return;
      }

      // Ordenar eventos por fecha
      const eventosOrdenados = [...eventos.data].sort((a, b) => {
        const fechaA = a.fechas?.[0]?.fecha || a.created_at;
        const fechaB = b.fechas?.[0]?.fecha || b.created_at;
        return new Date(fechaA) - new Date(fechaB);
      });

      // Crear estructura de datos para Excel
      const datosExcel = [];
      let contador = 1;

      eventosOrdenados.forEach((evento) => {
        // Si el evento tiene múltiples fechas, crear una fila por cada fecha
        if (evento.fechas && evento.fechas.length > 0) {
          evento.fechas.forEach((fecha) => {
            const fechaEvento = new Date(fecha.fecha);
            const diaSemana = format(fechaEvento, "EEEE", { locale: es });
            const diaNumero = format(fechaEvento, "d", { locale: es });
            const mes = format(fechaEvento, "MMMM", { locale: es });
            const año = format(fechaEvento, "yyyy", { locale: es });
            const hora = fecha.hora || "Sin hora";

            datosExcel.push({
              "#": contador++,
              Fecha: `${diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1)} ${diaNumero} de ${mes} de ${año}`,
              Hora: hora,
              Título: evento.nombre,
              Sala: evento.nombre_sitio || "Sin sala",
              Dirección: evento.direccion || "Sin dirección",
              Provincia: evento.provincia || "Sin provincia",
              Categorías: evento.categorias?.map((c) => c.nombre).join(", ") || "Sin categorías",
              Estado: evento.status === 1 ? "Activo" : evento.status === 2 ? "Pausado" : evento.status === 3 ? "Eliminado" : "Borrador",
            });
          });
        } else {
          // Si no tiene fechas, usar la fecha de creación
          const fechaEvento = new Date(evento.created_at);
          const diaSemana = format(fechaEvento, "EEEE", { locale: es });
          const diaNumero = format(fechaEvento, "d", { locale: es });
          const mes = format(fechaEvento, "MMMM", { locale: es });
          const año = format(fechaEvento, "yyyy", { locale: es });

          datosExcel.push({
            "#": contador++,
            Fecha: `${diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1)} ${diaNumero} de ${mes} de ${año}`,
            Hora: "Sin hora",
            Título: evento.nombre,
            Sala: evento.nombre_sitio || "Sin sala",
            Dirección: evento.direccion || "Sin dirección",
            Provincia: evento.provincia || "Sin provincia",
            Categorías: evento.categorias?.map((c) => c.nombre).join(", ") || "Sin categorías",
            Estado: evento.status === 1 ? "Activo" : evento.status === 2 ? "Pausado" : evento.status === 3 ? "Eliminado" : "Borrador",
          });
        }
      });

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      ws["!cols"] = [
        { wch: 5 },   // #
        { wch: 35 },  // Fecha
        { wch: 10 },  // Hora
        { wch: 40 },  // Título
        { wch: 25 },  // Sala
        { wch: 35 },  // Dirección
        { wch: 15 },  // Provincia
        { wch: 25 },  // Categorías
        { wch: 12 },  // Estado
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Eventos");

      // Descargar archivo
      const nombreArchivo = `eventos_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);

      setLoading(false);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al exportar el archivo Excel");
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div
        className="row mb-4"
        style={{
          marginLeft: isDesktop ? "10vw" : "0px",
          marginRight: isDesktop ? "10vw" : "0px",
        }}
      >
        <div className="col-md-6">
          <h1 className="h2 fw-bold">EVENTOS</h1>
        </div>
        <div className="col-md-6 text-md-end">
          <button
            className="d-none btn btn-info text-white me-2"
            onClick={generarImagenes}
            disabled={loading}
          >
            <i className="bi bi-images me-2"></i>GENERAR IMÁGENES
          </button>
          <button
            className="btn btn-success text-white me-2"
            onClick={exportarExcel}
            disabled={loading}
          >
            <i className="bi bi-file-earmark-excel me-2"></i>EXPORTAR EXCEL
          </button>
          <button
            className="btn btn-custom-naranja text-white"
            name="añadir-evento"
            onClick={(event) => onClick(event)}
          >
            <i className="bi bi-plus me-2"></i>NUEVO EVENTO
          </button>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          marginLeft: isDesktop ? "10vw" : "0px",
          marginRight: isDesktop ? "10vw" : "0px",
        }}
      >
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className={`col-md-3`}>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Buscar"
                  onChange={(e) => onChange(e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      onClick({ target: { name: "search" } });
                  }}
                />
              </div>
            </div>
            <div
              className={`col-md-3 ${user?.roles[0].nombre == "adm" ? "" : "d-none"
                }`}
            >
              <select
                className="form-select"
                onChange={(e) => {
                  setLoading(true);
                  if (e.target.value == "todos") {
                    let params = filter;
                    delete params["filter[provincia]"];
                    setFilter({ ...params });
                    return fetch(setData, params, "/eventos");
                  }

                  setLoading(true);
                  setFilter({
                    ...filter,
                    ["filter[provincia]"]: e.target.value,
                  });
                  let params = {
                    ...filter,
                    ["filter[provincia]"]: e.target.value,
                  };

                  fetch(setData, params, "/eventos", null, setLoading);
                  fetch(
                    setSalas,
                    { ["filter[provincia]"]: e.target.value },
                    "/salas"
                  );
                }}
              >
                {"filter[provincia]" in filter && provincias.length > 0 ? (
                  <option selected>
                    {
                      provincias.filter(
                        (p) => p.provincia == filter["filter[provincia]"]
                      )[0].provincia
                    }
                  </option>
                ) : (
                  <option selected>Seleccionar provincia</option>
                )}

                {[...provincias, { provincia: "todos", total_eventos: "" }].map(
                  (provincia) => {
                    if ("filter[provincia]" in filter) {
                      if (filter["filter[provincia]"] == provincia.provincia) {
                        return "";
                      }
                    }
                    return (
                      <option
                        value={provincia.provincia}
                      >{`${provincia.provincia} (${provincia.total_eventos})`}</option>
                    );
                  }
                )}
              </select>
            </div>
            <div
              className={`col-md-3 ${user?.roles[0].nombre == "adm" ? "" : "d-none"
                }`}
            >
              <select
                className="form-select"
                onChange={(e) => {
                  setLoading(true);
                  if (e.target.value == "todos") {
                    let params = filter;
                    delete params["filter[nombre_sitio]"];
                    setFilter({ ...params });
                    return fetch(setData, params, "/eventos");
                  }

                  setFilter({
                    ...filter,
                    ["filter[nombre_sitio]"]: e.target.value,
                  });
                  let params = {
                    ...filter,
                    ["filter[nombre_sitio]"]: e.target.value,
                  };
                  fetch(setData, params, "/eventos");
                }}
              >
                {"filter[nombre_sitio]" in filter ? (
                  <option selected>
                    {
                      salas.data.filter(
                        (c) => c.nombre == filter["filter[nombre_sitio]"]
                      )[0].nombre
                    }
                  </option>
                ) : (
                  <option selected>Seleccionar sala</option>
                )}
                {[...salas.data, { nombre: "todos" }].map((sala) => {
                  if ("filter[nombre_sitio]" in filter) {
                    if (filter["filter[nombre_sitio]"] == sala.nombre) {
                      return "";
                    }
                  }
                  return (
                    <option value={sala.nombre}>{`${sala.nombre}`}</option>
                  );
                })}
              </select>
            </div>

            <div
              className={`col-md-3 ${user?.roles[0].nombre == "adm" ? "" : "d-none"
                }`}
            >
              <select
                className="form-select"
                onChange={(e) => {
                  setLoading(true);

                  if (e.target.value == "todos") {
                    let params = filter;
                    delete params["filter[category]"];
                    setFilter({ ...params });
                    return fetch(setData, params, "/eventos");
                  }
                  setFilter({
                    ...filter,
                    ["filter[category]"]: e.target.value,
                  });
                  let params = {
                    ...filter,
                    ["filter[category]"]: e.target.value,
                  };
                  fetch(setData, params, "/eventos");
                }}
              >
                {"filter[category]" in filter ? (
                  <option selected>
                    {
                      categorias.filter(
                        (c) => c.id == filter["filter[category]"]
                      )[0].nombre
                    }
                  </option>
                ) : (
                  <option selected>Seleccionar categoria</option>
                )}
                {[...categorias, { id: "todos", nombre: "TODOS" }].map(
                  (categoria) => {
                    if ("filter[category]" in filter) {
                      if (filter["filter[category]"] == categoria.id) {
                        return "";
                      }
                    }
                    return (
                      <option value={categoria.id}>{categoria.nombre}</option>
                    );
                  }
                )}
              </select>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Desde</label>
              <input
                type="date"
                className="form-control"
                defaultValue={filter["filter[fecha]"].split(",")[0]}
                value={filter["filter[fecha]"].split(",")[0]}
                onChange={(e) => {
                  setLoading(true);
                  let hasta = filter.hasOwnProperty("filter[fecha]")
                    ? filter["filter[fecha]"].split(",")[1]
                    : format(new Date(), "yyyy-MM-dd").toString();

                  let params = {
                    ...filter,
                    ["filter[fecha]"]: `${e.target.value},${hasta}`,
                  };

                  setFilter({ ...params });
                  return fetch(setData, params, "/eventos");
                }}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Hasta</label>
              <input
                type="date"
                className="form-control"
                defaultValue={filter["filter[fecha]"].split(",")[1]}
                value={filter["filter[fecha]"].split(",")[1]}
                onChange={(e) => {
                  setLoading(true);
                  let desde = filter.hasOwnProperty("filter[fecha]")
                    ? filter["filter[fecha]"].split(",")[0]
                    : format(new Date(), "yyyy-MM-dd").toString();

                  let params = {
                    ...filter,
                    ["filter[fecha]"]: `${desde},${e.target.value}`,
                  };

                  setFilter({ ...params });
                  return fetch(setData, params, "/eventos");
                }}
              />
            </div>
            <div
              className={`col-md-4`}
            >
              <label className="form-label fw-semibold">Estado</label>
              <select
                className="form-select"
                onChange={(e) => {
                  if (e.target.value == "todos") {
                    let params = filter;
                    delete params["status"];
                    setFilter({ ...params });
                    return fetch(setData, params, "/eventos");
                  }
                  setFilter({ ...filter, status: e.target.value });
                  let params = { ...filter, status: e.target.value };
                  fetch(setData, params, "/eventos");
                }}
              >
                {"status" in filter ? (
                  <option selected>
                    {
                      [
                        { value: "Todos", label: "Todos" },
                        { value: "1", label: "Activo" },
                        { value: "2", label: "Pausado" },
                        { value: "3", label: "Eliminado" },
                        { value: "4", label: "Borrador" },
                      ].filter((c) => c.value == filter.status)[0].label
                    }
                  </option>
                ) : (
                  <option selected>Seleccionar estado</option>
                )}

                {[
                  { value: "Todos", label: "Todos" },
                  { value: "1", label: "Activo" },
                  { value: "2", label: "Pausado" },
                  { value: "3", label: "Eliminado" },
                  { value: "4", label: "Borrador" },
                ].map((status) => {
                  if ("status" in filter) {
                    if (filter.status == status.value) {
                      return "";
                    }
                  }
                  return <option value={status.value}>{status.label}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        className="d-flex mb-2"
        style={{
          borderRadius: "0.3rem",
          marginLeft: isDesktop ? "10vw" : "0px",
          marginRight: isDesktop ? "10vw" : "0px",
          backgroundColor: "#eaeaea",
        }}
      >
        <div className="p-2 w-100">
          <label
            className="btn btn-light border-0 w-100 "
            style={{
              backgroundColor: anterior ? "" : "#eaeaea",
              color: anterior ? "" : "#ccced1",
            }}
          >
            Eventos anteriores
            <button
              className="d-none"
              type="button"
              role="tab"
              onClick={(e) => {
                setLoading(true);
                let desde = format(
                  subYears(new Date(), 1),
                  "yyyy-MM-dd"
                );
                let hasta = format(new Date(), "yyyy-MM-dd").toString();

                let params = {
                  ...filter,
                  ["filter[fecha]"]: `${desde},${hasta}`,
                };

                setAnterior(true);
                setFilter({ ...params });
                return fetch(setData, params, "/eventos");
              }}
            ></button>
          </label>
        </div>
        <div className="p-2 w-100">
          <label
            className="btn btn-light  border-0 w-100"
            style={{
              backgroundColor: anterior ? "#eaeaea" : "",
              color: anterior ? "#ccced1" : "",
            }}
          >
            Eventos próximos
            <button
              className="d-none"
              type="button"
              role="tab"
              onClick={(e) => {
                setLoading(true);
                let desde = format(new Date(), "yyyy-MM-dd").toString();

                let params = {
                  ...filter,
                  ["filter[fecha]"]: `${desde}`,
                };

                setAnterior(false);
                setFilter({ ...params });
                return fetch(setData, params, "/eventos");
              }}
            ></button>
          </label>
        </div>
      </div>
    </div>
  );
};

export default FiltroEventos;
